import { memo } from 'react';
import { Loader2, Maximize2, Eye } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import type { BroadcastPreviewResult, BroadcastPayload } from '../types';
import type { NotificationChannel } from '@masters-club/types';
import { TelegramPreview, EmailPreview, AppPreview } from './channel-previews';

type LanguageRecord = {
  code: string;
  name: string;
  nativeName: string;
};

interface LivePreviewProps {
  channels: NotificationChannel[];
  languages: LanguageRecord[];
  previewLanguage: string;
  setPreviewLanguage: (value: string) => void;
  loading: boolean;
  error: string | null;
  result: BroadcastPreviewResult | null;
  payload: BroadcastPayload | null;
  mediaUrl?: string;
  onExpand: () => void;
}

export const LivePreview = memo(function LivePreview({
  channels,
  languages,
  previewLanguage,
  setPreviewLanguage,
  loading,
  error,
  result,
  payload,
  mediaUrl,
  onExpand,
}: LivePreviewProps) {
  const hasChannels = channels.length > 0;

  return (
    <div className="sticky top-4 space-y-3">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 text-sm font-semibold">
          <Eye className="h-4 w-4 text-muted-foreground" />
          Предпросмотр
          {loading && <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground" />}
        </div>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="h-7 w-7"
          onClick={onExpand}
          title="Развернуть"
        >
          <Maximize2 className="h-4 w-4" />
        </Button>
      </div>

      {languages.length > 0 && (
        <Select
          value={previewLanguage}
          onValueChange={setPreviewLanguage}
          disabled={languages.length === 0}
        >
          <SelectTrigger className="w-full">
            <SelectValue placeholder="Язык предпросмотра" />
          </SelectTrigger>
          <SelectContent>
            {languages.map((lang) => (
              <SelectItem key={lang.code} value={lang.code}>
                {lang.nativeName}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}

      <div className="rounded-lg border bg-muted/20 p-4 max-h-[70vh] overflow-y-auto">
        {!hasChannels ? (
          <p className="text-sm text-muted-foreground text-center py-8">
            Выберите канал, чтобы увидеть предпросмотр
          </p>
        ) : error ? (
          <div className="rounded-lg border border-destructive bg-destructive/10 p-3">
            <p className="text-sm text-destructive">{error}</p>
          </div>
        ) : !result ? (
          <p className="text-sm text-muted-foreground text-center py-8">
            {loading ? 'Загрузка предпросмотра...' : 'Введите текст сообщения'}
          </p>
        ) : (
          <div className="space-y-5">
            {channels.includes('TELEGRAM') && (
              <TelegramPreview
                message={result.message}
                mediaUrl={mediaUrl}
                buttons={payload?.telegram?.buttons}
              />
            )}
            {channels.includes('EMAIL') && (
              <EmailPreview
                message={result.message}
                buttons={payload?.email?.buttons}
                html={payload?.email?.html}
              />
            )}
            {channels.includes('APP') && (
              <AppPreview
                message={result.message}
                buttons={payload?.app?.buttons}
              />
            )}
          </div>
        )}
      </div>
    </div>
  );
});
